import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../components/AuthContext';
import api from '../api';
import AdminLayout from '../components/AdminLayout';
import './AdminMerchantPage.css';

const AdminMerchantPage = () => {
    const { user } = useContext(AuthContext);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState({});

    useEffect(() => {
        if (user && user.role === 'admin') {
            loadConnections();
        }
    }, [user]);

    const loadConnections = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/merchant/admin/connections');
            setConnections(response.data.connections || []);
        } catch (err) {
            setError('Errore nel caricamento delle connessioni');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmCSS = async (userId) => {
        if (!window.confirm('Sei sicuro di voler confermare la richiesta CSS per questo seller?')) return;

        try {
            setProcessing(prev => ({ ...prev, [userId]: true }));
            await api.post(`/merchant/admin/confirm-css/${userId}`);
            await loadConnections(); // Ricarica i dati
        } catch (err) {
            setError('Errore nella conferma CSS');
            console.error(err);
        } finally {
            setProcessing(prev => ({ ...prev, [userId]: false }));
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            google_only: { label: 'Solo Google', className: 'badge-info' },
            connected: { label: 'Merchant Collegato', className: 'badge-success' },
            requested: { label: 'CSS Richiesto', className: 'badge-warning' },
            css_request_sent: { label: 'Richiesta CSS Inviata', className: 'badge-info' },
            accepted: { label: 'CSS Attivo', className: 'badge-success' },
            disconnected: { label: 'Scollegato', className: 'badge-danger' }
        };

        const config = statusConfig[status] || { label: status, className: 'badge-secondary' };
        return <span className={`badge ${config.className}`}>{config.label}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getGoogleAdsStatus = (connection) => {
        if (connection.google_ads_customer_id && connection.google_ads_customer_name) {
            return (
                <div className="google-ads-info">
                    <div className="google-ads-status connected">
                        <span className="status-icon">✅</span>
                        <span>Collegato</span>
                    </div>
                    <div className="google-ads-details">
                        <div className="ads-name">{connection.google_ads_customer_name}</div>
                        <div className="ads-id">ID: {connection.google_ads_customer_id}</div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="google-ads-info">
                    <div className="google-ads-status not-connected">
                        <span className="status-icon">❌</span>
                        <span>Non Collegato</span>
                    </div>
                </div>
            );
        }
    };

    if (loading) {
        return (
            <div className="admin-merchant-page">
                <div className="loading">Caricamento...</div>
            </div>
        );
    }

    return (
        <AdminLayout>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ margin: 0 }}>Gestione Connessioni CSS</h2>
            </div>
            <p style={{ marginBottom: 24, color: '#6c757d' }}>Gestisci le richieste di passaggio a Loverlist CSS dei venditori</p>

                {error && (
                    <div className="alert alert-danger">
                        {error}
                        <button onClick={() => setError(null)} className="close-btn">&times;</button>
                    </div>
                )}

                <div className="stats-section">
                    <div className="stat-card">
                        <div className="stat-number">{connections.length}</div>
                        <div className="stat-label">Connessioni Totali</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{connections.filter(c => c.status === 'requested').length}</div>
                        <div className="stat-label">In Attesa</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{connections.filter(c => c.status === 'css_request_sent').length}</div>
                        <div className="stat-label">Processate</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{connections.filter(c => c.status === 'accepted').length}</div>
                        <div className="stat-label">Attive</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">{connections.filter(c => c.google_ads_customer_id).length}</div>
                        <div className="stat-label">Google Ads Collegati</div>
                    </div>
                </div>

                <div className="connections-section">
                    <h2>Connessioni Merchant</h2>
                    
                    {connections.length === 0 ? (
                        <div className="no-connections">
                            <p>Nessuna connessione merchant trovata.</p>
                        </div>
                    ) : (
                        <div className="connections-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Venditore</th>
                                        <th>Account Google</th>
                                        <th>Merchant Center</th>
                                        <th>Google Ads</th>
                                        <th>Status</th>
                                        <th>Data Creazione</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {connections.map(connection => (
                                        <tr key={connection.id}>
                                            <td>
                                                <div className="seller-info">
                                                    <div className="seller-name">
                                                        {connection.seller_name} {connection.seller_last_name}
                                                    </div>
                                                    <div className="seller-email">{connection.seller_email}</div>
                                                    {connection.store_name && (
                                                        <div className="store-name">🏪 {connection.store_name}</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="google-info">
                                                    <div className="google-name">{connection.google_name}</div>
                                                    <div className="google-email">{connection.google_email}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="merchant-info">
                                                    {connection.merchant_name ? (
                                                        <>
                                                            <div className="merchant-name">{connection.merchant_name}</div>
                                                            <div className="merchant-id">ID: {connection.merchant_id}</div>
                                                        </>
                                                    ) : (
                                                        <div className="merchant-not-connected">Non collegato</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {getGoogleAdsStatus(connection)}
                                            </td>
                                            <td>{getStatusBadge(connection.status)}</td>
                                            <td>{formatDate(connection.created_at)}</td>
                                            <td>
                                                {connection.status === 'requested' && (
                                                    <button 
                                                        className="btn btn-success btn-sm"
                                                        onClick={() => handleConfirmCSS(connection.user_id)}
                                                        disabled={processing[connection.user_id]}
                                                    >
                                                        {processing[connection.user_id] ? 'Processando...' : 'Conferma CSS'}
                                                    </button>
                                                )}
                                                {connection.status === 'accepted' && (
                                                    <span className="status-success">✅ CSS Attivo</span>
                                                )}
                                                {connection.status === 'css_request_sent' && (
                                                    <span className="status-info">📧 Richiesta Inviata</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </AdminLayout>
        );
};

export default AdminMerchantPage; 